/**
 * Payment Service - Handles all payment processing
 * Supports: Stripe, PayPal, Square, Bank Transfer
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Initialize Stripe
export const initializeStripe = async (publishableKey) => {
  try {
    const stripe = await window.Stripe(publishableKey);
    return stripe;
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    throw error;
  }
};

// Stripe Payment Processing
export const processStripePayment = async (paymentMethodId, amount, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/stripe`, {
      payment_method_id: paymentMethodId,
      amount: Math.round(amount * 100), // Convert to cents
      currency: 'usd',
      donation_data: donationData,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      transactionId: response.data.charge_id,
      amount: response.data.amount,
      status: response.data.status,
      receipt_url: response.data.receipt_url,
    };
  } catch (error) {
    console.error('Stripe payment error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Payment processing failed',
      code: error.response?.data?.code,
    };
  }
};

// PayPal Payment Processing
export const processPayPalPayment = async (amount, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/paypal`, {
      amount: Math.round(amount * 100),
      currency: 'usd',
      donation_data: donationData,
      return_url: `${window.location.origin}/donations/confirmation`,
      cancel_url: `${window.location.origin}/donations/payment`,
    });

    return {
      success: true,
      redirectUrl: response.data.approval_link,
      correlationId: response.data.correlation_id,
    };
  } catch (error) {
    console.error('PayPal payment error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'PayPal payment failed',
    };
  }
};

// Square Payment Processing
export const processSquarePayment = async (sourceId, amount, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/square`, {
      source_id: sourceId,
      amount: Math.round(amount * 100), // Convert to cents
      currency: 'USD',
      donation_data: donationData,
      idempotency_key: `${Date.now()}-${Math.random()}`,
    });

    return {
      success: true,
      transactionId: response.data.transaction_id,
      amount: response.data.amount,
      status: response.data.status,
      receipt_url: response.data.receipt_url,
    };
  } catch (error) {
    console.error('Square payment error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Square payment failed',
    };
  }
};

// Bank Transfer Setup
export const initiateBankTransfer = async (amount, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/bank-transfer`, {
      amount: Math.round(amount * 100),
      donation_data: donationData,
    });

    return {
      success: true,
      referenceId: response.data.reference_id,
      bankDetails: response.data.bank_details,
      deadline: response.data.deadline,
      confirmationEmail: response.data.confirmation_email_sent,
    };
  } catch (error) {
    console.error('Bank transfer error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Bank transfer setup failed',
    };
  }
};

// Create Payment Intent (Stripe)
export const createPaymentIntent = async (amount, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/stripe/intent`, {
      amount: Math.round(amount * 100),
      currency: 'usd',
      donation_data: donationData,
      metadata: {
        donor_name: donationData.donorName,
        donor_email: donationData.donorEmail,
        donation_type: donationData.frequency,
      },
    });

    return {
      success: true,
      clientSecret: response.data.client_secret,
      intentId: response.data.intent_id,
    };
  } catch (error) {
    console.error('Payment intent creation error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to create payment intent',
    };
  }
};

// Confirm Payment with 3D Secure
export const confirmPaymentWithSCA = async (clientSecret, stripe, elements) => {
  try {
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement('card'),
      },
    });

    if (result.error) {
      return {
        success: false,
        error: result.error.message,
      };
    }

    if (result.paymentIntent.status === 'succeeded') {
      return {
        success: true,
        transactionId: result.paymentIntent.id,
        status: result.paymentIntent.status,
      };
    }

    return {
      success: false,
      error: 'Payment confirmation required. Please complete 3D Secure.',
      requiresAction: true,
    };
  } catch (error) {
    console.error('SCA confirmation error:', error);
    return {
      success: false,
      error: 'Payment confirmation failed',
    };
  }
};

// Verify Payment
export const verifyPayment = async (transactionId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/payments/verify/${transactionId}`);

    return {
      success: true,
      status: response.data.status,
      amount: response.data.amount,
      processor: response.data.processor,
      createdAt: response.data.created_at,
    };
  } catch (error) {
    console.error('Payment verification error:', error);
    return {
      success: false,
      error: 'Failed to verify payment',
    };
  }
};

// Get Transaction History
export const getTransactionHistory = async (email, limit = 10) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/payments/history`, {
      params: { email, limit },
    });

    return {
      success: true,
      transactions: response.data.transactions || [],
      total: response.data.total,
    };
  } catch (error) {
    console.error('Transaction history error:', error);
    return {
      success: false,
      error: 'Failed to retrieve transaction history',
      transactions: [],
    };
  }
};

// Process Refund
export const processRefund = async (transactionId, reason = '') => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/refund`, {
      transaction_id: transactionId,
      reason,
    });

    return {
      success: true,
      refundId: response.data.refund_id,
      amount: response.data.amount,
      status: response.data.status,
    };
  } catch (error) {
    console.error('Refund error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Refund processing failed',
    };
  }
};

// Setup Recurring Donation
export const setupRecurringDonation = async (amount, frequency, paymentMethodId, donationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/recurring`, {
      amount: Math.round(amount * 100),
      frequency, // 'monthly', 'quarterly', 'annual'
      payment_method_id: paymentMethodId,
      donation_data: donationData,
    });

    return {
      success: true,
      subscriptionId: response.data.subscription_id,
      status: response.data.status,
      nextBillingDate: response.data.next_billing_date,
      confirmationEmail: response.data.confirmation_email_sent,
    };
  } catch (error) {
    console.error('Recurring donation setup error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to setup recurring donation',
    };
  }
};

// Cancel Recurring Donation
export const cancelRecurringDonation = async (subscriptionId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/recurring/cancel`, {
      subscription_id: subscriptionId,
    });

    return {
      success: true,
      status: response.data.status,
      cancelledAt: response.data.cancelled_at,
    };
  } catch (error) {
    console.error('Cancel recurring error:', error);
    return {
      success: false,
      error: 'Failed to cancel recurring donation',
    };
  }
};

// Update Payment Method
export const updatePaymentMethod = async (subscriptionId, paymentMethodId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/payments/recurring/update-method`, {
      subscription_id: subscriptionId,
      payment_method_id: paymentMethodId,
    });

    return {
      success: true,
      status: response.data.status,
      updatedAt: response.data.updated_at,
    };
  } catch (error) {
    console.error('Update payment method error:', error);
    return {
      success: false,
      error: 'Failed to update payment method',
    };
  }
};

// Calculate Fees
export const calculateFees = (amount, processor) => {
  const fees = {
    stripe: { percentage: 2.2, fixed: 0.3 },
    paypal: { percentage: 2.2, fixed: 0.3 },
    square: { percentage: 2.6, fixed: 0.3 },
    donorbox: { percentage: 1.5, fixed: 0.3 },
    'give-org': { percentage: 2, fixed: 0 },
    bank: { percentage: 0, fixed: 0 },
  };

  const processorKey = processor ? processor.toLowerCase() : 'stripe';
  const fee = fees[processorKey] || { percentage: 2.2, fixed: 0.3 };
  const feeAmount = (amount * fee.percentage) / 100 + fee.fixed;

  return {
    processor,
    subtotal: amount,
    fees: parseFloat(feeAmount.toFixed(2)),
    total: parseFloat((amount + feeAmount).toFixed(2)),
    percentage: fee.percentage,
    fixed: fee.fixed,
  };
};

// Validate Card
export const validateCardNumber = (cardNumber) => {
  const sanitized = cardNumber.replace(/\D/g, '');
  if (!/^\d{13,19}$/.test(sanitized)) return false;

  let sum = 0;
  for (let i = 0; i < sanitized.length; i++) {
    let digit = parseInt(sanitized[i], 10);
    if ((sanitized.length - i - 1) % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

// Format Card Number
export const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  } else {
    return value;
  }
};

// Validate Expiry
export const validateExpiry = (expiry) => {
  const [month, year] = expiry.split('/');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const expiryYear = parseInt(`20${year}`, 10);
  const expiryMonth = parseInt(month, 10);

  if (expiryYear < currentYear) return false;
  if (expiryYear === currentYear && expiryMonth < currentMonth) return false;
  if (expiryMonth < 1 || expiryMonth > 12) return false;

  return true;
};

// Validate CVV
export const validateCVV = (cvv) => {
  return /^\d{3,4}$/.test(cvv.replace(/\D/g, ''));
};

const paymentServiceExports = {
  initializeStripe,
  processStripePayment,
  processPayPalPayment,
  processSquarePayment,
  initiateBankTransfer,
  createPaymentIntent,
  confirmPaymentWithSCA,
  verifyPayment,
  getTransactionHistory,
  processRefund,
  setupRecurringDonation,
  cancelRecurringDonation,
  updatePaymentMethod,
  calculateFees,
  validateCardNumber,
  formatCardNumber,
  validateExpiry,
  validateCVV,
};

export default paymentServiceExports;
